import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Button, List, ListItem, ListItemText, Tabs, Tab,
  TextField, Autocomplete, Table, TableHead, TableRow, TableCell, TableBody,
  FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Paper, Divider, InputAdornment, TableContainer,
  Tooltip, CircularProgress, Skeleton, Slide
} from '@mui/material';
import {
  Add, Delete, Edit, PictureAsPdf, Search as SearchIcon, Save, Close
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const TransitionUp = React.forwardRef(function TransitionUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const formatBR = (dateLike) => {
  if (!dateLike) return '—';
  const d = new Date(dateLike);
  return Number.isNaN(d.getTime()) ? String(dateLike) : d.toLocaleDateString('pt-BR');
};

const Inventario = () => {
  // ===== state
  const [contagens, setContagens] = useState([]);
  const [loadingContagens, setLoadingContagens] = useState(false);

  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('pending');

  const [items, setItems] = useState({ pending: [], done: [] });
  const [loadingItems, setLoadingItems] = useState(false);

  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [insumoOptions, setInsumoOptions] = useState([]);
  const [insumosLoading, setInsumosLoading] = useState(false);
  const [insumosQuery, setInsumosQuery] = useState('');
  const [selectedInsumo, setSelectedInsumo] = useState(null);
  const [quantity, setQuantity] = useState('');

  const [openNew, setOpenNew] = useState(false);
  const [newDate, setNewDate] = useState('');

  const [editItem, setEditItem] = useState(null);
  const [editQty, setEditQty] = useState('');

  // ===== effects
  useEffect(() => { fetchContagens(); }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => fetchInsumos(insumosQuery), 320);
    return () => clearTimeout(t);
  }, [insumosQuery]);

  useEffect(() => {
    if (!selected) return;
    fetchItems(selected.id, tab, debouncedSearch, categoryFilter);
    // Omite `fetchItems`; deps listam seleção/filtros.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, tab, debouncedSearch, categoryFilter]);

  // ===== api
  const fetchContagens = async () => {
    setLoadingContagens(true);
    try {
      const { data } = await api.get('/confereai/contagens', { params: { page: 1, per_page: 50 } });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setContagens(list);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar contagens');
    } finally {
      setLoadingContagens(false);
    }
  };

  const selectContagem = async (c) => {
    setSelected(c);
    setTab('pending');
    setSearch('');
    setCategoryFilter('');
    await fetchCategories(c.id);
    await fetchItems(c.id, 'pending');
    await fetchItems(c.id, 'done');
  };

  const fetchCategories = async (id) => {
    try {
      const { data } = await api.get(`/confereai/contagens/${id}/categorias/pendentes`);
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setCategories(list);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar categorias');
    }
  };

  const fetchItems = async (id, status, q = '', cat = '') => {
    setLoadingItems(true);
    try {
      const { data } = await api.get(`/confereai/contagens/${id}/itens`, {
        params: { status, q: q || undefined, categoria_id: cat || undefined, page: 1, per_page: 100 },
      });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setItems((prev) => ({ ...prev, [status]: list }));
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar itens');
      setItems((prev) => ({ ...prev, [status]: [] }));
    } finally {
      setLoadingItems(false);
    }
  };

  const fetchInsumos = async (q = '') => {
    setInsumosLoading(true);
    try {
      const { data } = await api.get('/confereai/insumos', { params: { q: q || undefined, per_page: 12 } });
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setInsumoOptions(list);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao buscar insumos');
    } finally {
      setInsumosLoading(false);
    }
  };

  // ===== actions
  const handleNewContagem = async () => {
    try {
      const { data } = await api.post('/confereai/contagens', {
        data: newDate || new Date().toISOString().slice(0, 10),
      });
      toast.success('Contagem criada');
      setOpenNew(false);
      await fetchContagens();
      await selectContagem(data);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao criar contagem');
    }
  };

  const handleAddItem = async () => {
    if (!selected || !selectedInsumo || !quantity) return;
    try {
      await api.post(`/confereai/contagens/${selected.id}/itens`, [
        { insumo_id: selectedInsumo.id, quantidade: parseFloat(quantity) },
      ]);
      toast.success('Item adicionado');
      setSelectedInsumo(null);
      setQuantity('');
      fetchItems(selected.id, tab, debouncedSearch, categoryFilter);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao adicionar item');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!selected) return;
    try {
      await api.delete(`/confereai/contagens/${selected.id}/itens/${item.insumo_id || item.id}`);
      toast.success('Item removido');
      fetchItems(selected.id, tab, debouncedSearch, categoryFilter);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao remover item');
    }
  };

  const handleEditItem = async () => {
    if (!selected || !editItem) return;
    try {
      await api.put(
        `/confereai/contagens/${selected.id}/itens/${editItem.insumo_id || editItem.id}`,
        { quantidade: parseFloat(editQty) }
      );
      toast.success('Item atualizado');
      setEditItem(null);
      setEditQty('');
      fetchItems(selected.id, tab, debouncedSearch, categoryFilter);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao atualizar item');
    }
  };

  const handleFinalize = async () => {
    if (!selected) return;
    if (!window.confirm('Finalizar contagem?')) return;
    try {
      await api.patch(`/confereai/contagens/${selected.id}/finalizar`);
      toast.success('Contagem finalizada');
      await fetchContagens();
      await selectContagem(selected);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao finalizar contagem');
    }
  };

  const handlePdf = () => {
    if (selected) window.open(`/confereai/contagens/${selected.id}/pdf`, '_blank');
  };

  const currentItems = items[tab] || [];

  // ===== UI
  return (
    <Box sx={{ px: { xs: 3, sm: 5 }, pb: 3 }}>
      {/* Header (mesmo padrão) */}
      <Box sx={{ mb: 3, py: 1.5, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: 0.3
          }}
        >
          Inventário
        </Typography>

        <Button variant="contained" startIcon={<Add />} onClick={() => setOpenNew(true)}>
          Nova Contagem
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Lateral: contagens */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            elevation={0}
            sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
          >
            <Box mb={1.5} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Suas contagens</Typography>
              <Tooltip title="Criar contagem">
                <IconButton size="small" onClick={() => setOpenNew(true)} aria-label="Criar contagem">
                  <Add />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <List dense sx={{ maxHeight: 500, overflowY: 'auto' }}>
              {loadingContagens ? (
                [...Array(6)].map((_, i) => (
                  <ListItem key={`sk-${i}`}>
                    <ListItemText primary={<Skeleton width="80%" />} secondary={<Skeleton width="50%" />} />
                  </ListItem>
                ))
              ) : contagens.length ? (
                contagens.map((c) => (
                  <ListItem
                    key={c.id}
                    button
                    selected={selected?.id === c.id}
                    onClick={() => selectContagem(c)}
                    sx={{ borderRadius: 2, mb: 0.5, '&.Mui-selected': { bgcolor: 'action.selected' } }}
                  >
                    <ListItemText primary={c.nome || `Contagem ${c.id}`} secondary={formatBR(c.data)} />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="Nenhuma contagem" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Conteúdo principal */}
        <Grid item xs={12} md={8} lg={9}>
          {selected ? (
            <Box component={motion.div} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              {/* Painel: tabs + filtros (padrão) */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2.5,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(250,250,250,0.7))',
                  backdropFilter: 'blur(8px)'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5} flexWrap="wrap">
                  <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 3 } }}>
                    <Tab label="Pendentes" value="pending" />
                    <Tab label="Contabilizados" value="done" />
                  </Tabs>
                  <Box display="flex" gap={1}>
                    <Button onClick={handlePdf} startIcon={<PictureAsPdf />}>PDF</Button>
                    <Button variant="contained" onClick={handleFinalize}>Finalizar</Button>
                  </Box>
                </Box>

                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Buscar item"
                      fullWidth
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Categoria</InputLabel>
                      <Select
                        label="Categoria"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                      >
                        <MenuItem value=""><em>Todas categorias</em></MenuItem>
                        {categories.map((c) => (
                          <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>

              {/* Adição rápida (padrão) */}
              <Paper
                elevation={0}
                sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
              >
                <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                  <Autocomplete
                    sx={{ flex: 1, minWidth: 260 }}
                    options={insumoOptions}
                    loading={insumosLoading}
                    onOpen={() => fetchInsumos('')}
                    getOptionLabel={(o) => o?.nome || ''}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                    onInputChange={(_, v) => setInsumosQuery(v)}
                    onChange={(_, v) => setSelectedInsumo(v)}
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Insumo"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {insumosLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <TextField
                    label="Quantidade"
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={{ width: 150 }}
                  />
                  <Button variant="contained" startIcon={<Add />} onClick={handleAddItem}>
                    Adicionar
                  </Button>
                </Box>
              </Paper>

              {/* Tabela (padrão) */}
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  background: '#fff'
                }}
              >
                <Table
                  size="small"
                  sx={{
                    '& thead th': { bgcolor: 'grey.50', fontWeight: 600 },
                    '& th, & td': { px: 2 },
                    '& th:first-of-type, & td:first-of-type': { pl: 3 },
                    '& th:last-of-type,  & td:last-of-type':  { pr: 3 },
                    '& thead th:first-of-type': { borderTopLeftRadius: 12 },
                    '& thead th:last-of-type':  { borderTopRightRadius: 12 },
                    '& tbody tr:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell width={180}>Quantidade</TableCell>
                      <TableCell width={120} align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <AnimatePresence initial={false}>
                      {loadingItems ? (
                        [...Array(6)].map((_, i) => (
                          <TableRow key={`sk-${i}`}>
                            <TableCell><Skeleton height={20} /></TableCell>
                            <TableCell><Skeleton height={20} width="40%" /></TableCell>
                            <TableCell align="right"><Skeleton height={24} width={80} /></TableCell>
                          </TableRow>
                        ))
                      ) : currentItems.length ? (
                        currentItems.map((it) => (
                          <TableRow
                            key={it.insumo_id || it.id}
                            component={motion.tr}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.15 }}
                          >
                            <TableCell>{it.insumo_nome || it.nome}</TableCell>
                            <TableCell>{it.quantidade}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Editar">
                                <IconButton size="small" onClick={() => { setEditItem(it); setEditQty(it.quantidade); }} aria-label="Editar item da contagem">
                                  <Edit fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton size="small" onClick={() => handleDeleteItem(it)} aria-label="Excluir item da contagem">
                                  <Delete fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                            <Typography color="text.secondary">Nenhum item</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </AnimatePresence>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}
            >
              <Typography color="text.secondary">Selecione uma contagem ou crie uma nova.</Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Modal Nova Contagem (padrão) */}
      <Dialog
        open={openNew}
        onClose={() => setOpenNew(false)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={TransitionUp}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 14, scale: 0.98 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: 14, scale: 0.98 },
          transition: { duration: 0.2 },
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Add fontSize="small" /> Nova Contagem
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Data"
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button startIcon={<Close />} onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button startIcon={<Save />} variant="contained" onClick={handleNewContagem}>Criar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar Quantidade (padrão) */}
      <Dialog
        open={!!editItem}
        onClose={() => setEditItem(null)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={TransitionUp}
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 14, scale: 0.98 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: 14, scale: 0.98 },
          transition: { duration: 0.2 },
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle>Editar Quantidade</DialogTitle>
        <Divider />
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Quantidade"
            type="number"
            value={editQty}
            onChange={(e) => setEditQty(e.target.value)}
            inputProps={{ step: '0.01', min: '0' }}
            fullWidth
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button startIcon={<Close />} onClick={() => setEditItem(null)}>Cancelar</Button>
          <Button startIcon={<Save />} variant="contained" onClick={handleEditItem}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventario;
